import { Router } from '@angular/router';
import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UntypedFormBuilder, Validators, UntypedFormGroup, UntypedFormControl, AbstractControl } from '@angular/forms';
import * as moment from 'moment';
import { UiService } from '@services/ui.service';
import { Order } from '@classes/order/order';
import { OrdersService } from '@services/orders.service';
import { AuthService } from '@services/auth.service';
import { RoleName } from '@enums/role-name.enum';
import { UsersService } from '@services/users.service';
import { ConfirmValidMatcher } from '@classes/validations/confirm-valid-matcher';
import { Client } from '@classes/client';
import { ClientsService } from '@services/clients.service';
import { Site } from '@classes/site';
import { SitesService } from '@services/sites.service';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { Address } from '@classes/address';
import { AddressesService } from '@services/addresses.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, startWith } from 'rxjs/operators';
import { User } from '@classes/user/user';
import { OrderType } from '@enums/order-type.enum';

@Component({
  selector: 'app-order-transmission-edit',
  templateUrl: './order-transmission-edit.component.html',
  styleUrls: ['./order-transmission-edit.component.scss']
})
export class OrderTransmissionEditComponent extends FormEditBaseComponent implements OnInit {

  clients: Client[];
  sites: Site[];
  filteredSites: Site[] = [];
  selectedClient: Client;
  allTransportTypes: DictionaryValue[];
  urgencyLevels: DictionaryValue[];
  senders: Address[];
  receivers: Address[];
  currentUser?: User;

  @Input() order: Order;
  isEditMode = false;

  stringMaxLength = 50;
  commentMaxLength = 500;
  isRestricted: boolean;
  isClient: boolean;
  isAdmin: boolean;
  isDispatcher: boolean;
  isAdminWithOneClient: boolean;
  mustCheckDate = false;
  confirmValidMatcher = new ConfirmValidMatcher();

  invalidFields: string[] = [];
  invalidMessage = '';

  transportTypesSelected: DictionaryValue[] = [];
  devices: DictionaryValue[];
  filteredTransportTypes: Observable<DictionaryValue[]> = of([]);
  transportTypesCtrl = new UntypedFormControl();
  selectable = true;
  removable = true;
  addOnBlur = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  clientForm = new UntypedFormGroup({
    client: new UntypedFormControl('', Validators.required)
  })

  isFormReady = false;
  canAddOtherAddress = true;

  @ViewChild('deviceInput') deviceInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private authSrv: AuthService,
    private usersSrv: UsersService,
    private clientsSrv: ClientsService,
    private ordersSrv: OrdersService,
    private sitesSrv: SitesService,
    private dictionarySrv: DictionaryValuesService,
    private addressesSrv: AddressesService,
    private router: Router
  ) {
    super(uiSrv, fb);

  }

  ngOnInit(): void {
    super.ngOnInit();

    this.isEditMode = this.order?.id !== undefined;
    this.resolveRoles();

    if (!this.isEditMode) {
      this.addSubscription(
        this.clientForm.get('client').valueChanges
          .pipe(
            filter(value => !!value && typeof value !== 'string' && value.id !== this.selectedClient?.id)
          )
          .subscribe((client: Client) => this.onClientSelected(client))
      );
    }

    this.initData();
  }

  private resolveRoles(): void {
    const roleName = this.authSrv.getUser().role.name;

    this.isRestricted = roleName !== RoleName.super_admin && roleName !== RoleName.bizzdev;
    this.isClient = roleName === RoleName.client;
    this.isAdmin =
      roleName === RoleName.bizzdev ||
      roleName === RoleName.super_admin ||
      roleName === RoleName.admin ||
      roleName === RoleName.admin_subcontractor;
    this.isDispatcher = roleName !== RoleName.client && roleName !== RoleName.admin_client;

    const hasFullAccess = roleName === RoleName.bizzdev || roleName === RoleName.super_admin;
    const tokenClients: string[] = this.authSrv.getDecodedToken().client.split(',');

    this.isAdminWithOneClient = !hasFullAccess && tokenClients.length === 1;
  }

  isControlSet(control: string): boolean {
    return !!this.editForm?.get(control);
  }

  onFinish(): void {
    this.editFinished.emit();

    if (!this.isEditMode) {
      this.router.navigate(['/orders/list']);
    }
  }

  onSubmit(): void {
    if (this._isDatesValid()) {
      this.isLoading = true;
      if (this.isEditMode) {
        this.update();
      } else {
        this.create();
      }
    }
  }

  private _isDatesValid(): boolean {
    if (!this.mustCheckDate) {
      return true;
    }

    const startDate = new Date(this.editForm.controls.schedule.value[0].startDate);
    const startTime = this.editForm.controls.schedule.value[0].startTime.split(':');

    if (new Date(startDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
      alert('La date ne peut pas être antérieure à la date du jour.');
      return false;
    }

    startDate.setHours(startTime[0], startTime[1]);

    if (startDate.getTime() - new Date().getTime() <= 20 * 60 * 1000) {
      alert("L'heure de début doit être au moins 20 minutes après l'heure actuelle.");
      return false;
    }

    return true;
  }

  private create(): void {
    const formValues = this.editForm.value;

    const sitesPromise = new Promise((resolve, reject) => {
      if (typeof formValues.site === 'string' && this.isDispatcher) {
        let newSite = new Site(
          formValues.site,
          [this.urgencyLevels[0].client],
          false
        );

        this.sitesSrv.post(newSite).subscribe(newSiteFromSrv => {
          formValues.site = newSiteFromSrv;
          resolve(true);
        })
      } else {
        resolve(true);
      }
    });

    Promise.all([sitesPromise])
      .then(() => {
        this.normalizeSteps(formValues);

        this.order = new Order(
          formValues.site,
          formValues.urgencyLevel,
          this.scheduleToDate(formValues.schedule[0]),
          formValues.ordererPhone,
          formValues.ordererName?.trim(),
          formValues.sender,
          formValues.receivers,
          this.toPayloadTransportTypes(),
          OrderType.transmission,
          formValues.ordererComment,
          this.toCallDate(formValues.callDate)
        );

        this.order.client = this.clientForm.get('client')?.value;

        this.ordersSrv.post(this.order)
          .subscribe(
            (orderCreated) => {
              this.isLoading = false;
              this.uiSrv.showSnackbar('orders.create.created', true);
              this.order = orderCreated;

              this.onFinish();
            },
            () => this.order = null
          );
      });
  }

  private normalizeSteps(formValues: any): void {
    if (typeof formValues.sender.address === 'string') {
      formValues.sender.otherAddress = formValues.sender.address;
      formValues.sender.address = null;
    } else {
      formValues.sender.otherAddress = null;
    }

    formValues.receivers.forEach(receiver => {
      if (typeof receiver.address === 'string') {
        receiver.otherAddress = receiver.address;
        receiver.address = null;
      } else {
        receiver.otherAddress = null;
      }

      receiver.isDelivery = receiver.type === 'isDelivery';
      receiver.isPickup = receiver.type !== 'isDelivery';
    });
  }

  private update(): void {
    const formValues = this.editForm.value;
    const orderCopy: Order = JSON.parse(JSON.stringify(this.order));

    this.normalizeSteps(formValues);

    orderCopy.site = formValues.site;
    orderCopy.urgency = formValues.urgencyLevel;
    orderCopy.startDate = this.scheduleToDate(formValues.schedule[0]) ?? orderCopy.startDate;
    orderCopy.ordererPhone = formValues.ordererPhone;
    orderCopy.ordererName = formValues.ordererName?.trim();
    orderCopy.ordererComment = formValues.ordererComment;
    // On remplace l'étape complète : le PUT n'accepte pas les champs de suivi (scans, waitingTime, signatures).
    orderCopy.sender = formValues.sender;
    orderCopy.receivers = formValues.receivers;
    orderCopy.transportTypes = this.toPayloadTransportTypes();
    orderCopy.callDate = this.toCallDate(formValues.callDate);

    orderCopy.patients = orderCopy.patients ?? [];
    orderCopy.name = orderCopy.name ?? null;
    orderCopy.requestedDeliveryDate = orderCopy.requestedDeliveryDate ?? null;

    orderCopy.modifiedFields = this.checkWhatChanged(orderCopy);

    this.ordersSrv.put(orderCopy)
      .subscribe(() => {
        this.isLoading = false;
        this.uiSrv.showSnackbar('orders.edit.edited', true);
        this.onFinish();
      });
  }

  private checkWhatChanged(orderCopy: Order): string[] {
    const modifiedFields: string[] = [];

    if (this.order.site?.id !== orderCopy.site?.id) {
      modifiedFields.push('Donneur d\'ordre');
    }

    if (this.order.urgency?.id !== orderCopy.urgency?.id) {
      modifiedFields.push('Niveau d\'urgence');
    }

    const previousTypes = this.order.transportTypes ?? [];
    const currentTypes = orderCopy.transportTypes ?? [];

    if (previousTypes.length !== currentTypes.length
      || currentTypes.some((type, index) => previousTypes[index].id !== type.id)) {
      modifiedFields.push('Type de produit');
    }

    if (new Date(this.order.startDate).toString() !== new Date(orderCopy.startDate).toString()) {
      modifiedFields.push('Date, heure de départ souhaitée');
    }

    if (this.order.ordererComment !== orderCopy.ordererComment) {
      modifiedFields.push('Commentaire sur la course');
    }

    if (this.order.ordererName !== orderCopy.ordererName) {
      modifiedFields.push('Nom du demandeur');
    }

    if (this.order.ordererPhone !== orderCopy.ordererPhone) {
      modifiedFields.push('Téléphone du demandeur');
    }

    modifiedFields.push(...this.checkStepChanged(this.order.sender, orderCopy.sender, 1));

    if (this.order.receivers?.length !== orderCopy.receivers?.length) {
      modifiedFields.push('Étape(s)');
    } else {
      orderCopy.receivers.forEach((receiver, index) => {
        modifiedFields.push(...this.checkStepChanged(this.order.receivers[index], receiver, index + 2));
      });
    }

    return modifiedFields.map((field, index) => index === 0 ? field : ' ' + field);
  }

  private checkStepChanged(previous: any, current: any, position: number): string[] {
    const modifiedFields: string[] = [];

    const previousAddress = previous?.address?.id ?? previous?.otherAddress ?? null;
    const currentAddress = current?.address?.id ?? current?.otherAddress ?? null;

    if (previousAddress !== currentAddress) {
      modifiedFields.push('Adresse de l\'étape ' + position);
    }

    if ((previous?.service ?? null) !== (current?.service ?? null)) {
      modifiedFields.push('Service de l\'étape ' + position);
    }

    if ((previous?.ordererComment ?? null) !== (current?.ordererComment ?? null)) {
      modifiedFields.push('Commentaire de l\'étape ' + position);
    }

    return modifiedFields;
  }

  private toCallDate(callTime?: string): Date {
    if (!callTime) {
      return null;
    }

    const [hours, minutes] = callTime.split(':');
    return moment(new Date()).hours(+hours).minutes(+minutes).utc().toDate();
  }

  private toPayloadTransportTypes(): DictionaryValue[] {
    return this.transportTypesSelected.map(({ selected, isChecked, ...transportType }) => transportType as DictionaryValue);
  }

  // ---------------------------------------------------------------------------
  // Chargement des données
  // ---------------------------------------------------------------------------

  private initData(): void {
    this.isLoading = true;

    Promise.all([this.loadCurrentUser(), this.loadAddresses(), this.loadClients()])
      .then(() => {
        const client = this.resolveInitialClient();

        if (client) {
          this.selectedClient = client;
          this.clientForm.get('client').setValue(client, { emitEvent: false });
        }

        if (!this.isEditMode && !this.isAdminWithOneClient) {
          // L'utilisateur doit d'abord choisir une agence.
          this.isLoading = false;
          return null;
        }

        // En création mono-agence, les listes sont déjà restreintes côté serveur.
        return this.loadClientReferentials(this.isEditMode ? client : null)
          .then(() => this.isEditMode ? this.loadSiteReferentials(this.order.site, client?.id) : null)
          .then(() => {
            this.buildForm(this.isEditMode ? this.order : null);
            this.isFormReady = true;
            this.isLoading = false;
            this.applyDefaultSite(client);
          });
      });
  }

  private resolveInitialClient(): Client {
    if (this.isEditMode) {
      return this.clients?.find(client => client.id === this.order.client?.id) ?? this.order.client;
    }

    return this.isAdminWithOneClient && this.clients?.length === 1 ? this.clients[0] : null;
  }

  private loadCurrentUser(): Promise<void> {
    return new Promise<void>(resolve => {
      this.usersSrv.getCurrentUser().subscribe(userFromSrv => {
        this.currentUser = userFromSrv;
        resolve();
      });
    });
  }

  private loadAddresses(): Promise<void> {
    return new Promise<void>(resolve => {
      this.addressesSrv.getList().subscribe(addressesFromSrv => {
        this.senders = addressesFromSrv.filter(address => address.isSender);
        this.receivers = addressesFromSrv.filter(address => address.isReceiver);
        resolve();
      });
    });
  }

  private loadClients(): Promise<void> {
    return new Promise<void>(resolve => {
      this.clientsSrv.getTransmissionOnlyList().subscribe(clientsFromSrv => {
        this.clients = clientsFromSrv;
        resolve();
      });
    });
  }

  private loadClientReferentials(client: Client | null): Promise<void> {
    const sitesPromise = new Promise<void>(resolve => {
      const sites$ = client
        ? this.sitesSrv.getTransmissionOnlyListByClients([client.id])
        : this.sitesSrv.getTransmissionOnlyList();

      sites$.subscribe(sitesFromSrv => {
        this.sites = sitesFromSrv;
        this.filteredSites = sitesFromSrv;
        resolve();
      });
    });

    const transportTypesPromise = new Promise<void>(resolve => {
      const transports$ = client
        ? this.dictionarySrv.getTransportListById2(client.id)
        : this.dictionarySrv.getTransportList();

      transports$.subscribe(transportsFromSrv => {
        this.allTransportTypes = transportsFromSrv;
        this.initTransportTypesFilter();
        resolve();
      });
    });

    const urgencyLevelsPromise = new Promise<void>(resolve => {
      const urgencies$ = client
        ? this.dictionarySrv.getUrgencyListById2(client.id)
        : this.dictionarySrv.getUrgencyList();

      urgencies$.subscribe(urgencyLevelsFromSrv => {
        this.urgencyLevels = urgencyLevelsFromSrv;
        resolve();
      });
    });

    return Promise.all([sitesPromise, transportTypesPromise, urgencyLevelsPromise]).then(() => undefined);
  }

  private loadSiteReferentials(site: Site, clientId?: string): Promise<void> {
    this.canAddOtherAddress = !site || typeof site === 'string' || site.allowOtherAddress !== false;

    if (!site || typeof site === 'string' || !site.id) {
      return Promise.resolve();
    }

    const addressesPromise = new Promise<void>(resolve => {
      this.addressesSrv.getListForSite(site.id).subscribe(addresses => {
        this.senders = addresses.filter(address => address.isSender);
        this.receivers = addresses.filter(address => address.isReceiver);
        resolve();
      });
    });

    const transportTypesPromise = new Promise<void>(resolve => {
      this.dictionarySrv.getTransportListBySite(site, clientId).subscribe(transports => {
        this.allTransportTypes = transports;
        this.filteredTransportTypes = of(transports);
        resolve();
      });
    });

    const urgencyLevelsPromise = new Promise<void>(resolve => {
      this.dictionarySrv.getUrgencyListBySite(site, clientId).subscribe(urgencyLevels => {
        this.urgencyLevels = urgencyLevels;
        resolve();
      });
    });

    return Promise.all([addressesPromise, transportTypesPromise, urgencyLevelsPromise]).then(() => undefined);
  }

  private initTransportTypesFilter(): void {
    this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
      startWith(null),
      map((value: string | null) => value ? this._filter(value) : this.allTransportTypes.slice())
    );
  }

  // ---------------------------------------------------------------------------
  // Réactions aux actions utilisateur
  // ---------------------------------------------------------------------------

  private onClientSelected(client: Client): void {
    this.isLoading = true;
    this.selectedClient = client;

    this.loadClientReferentials(client).then(() => {
      if (this.editForm) {
        this.resetClientDependentFields();
      } else {
        this.buildForm(null);
      }

      this.isFormReady = true;
      this.isLoading = false;
      this.applyDefaultSite(client);
    });
  }

  /** Le donneur d'ordre par défaut de l'agence n'est appliqué qu'à la création. */
  private applyDefaultSite(client: Client): void {
    if (this.isEditMode) {
      return;
    }

    const defaultSite = this.sites?.find(site => site.id === client?.site?.id);

    if (defaultSite) {
      // Émission volontaire : onSiteSelected enchaîne sur les référentiels du site.
      this.editForm.get('site').setValue(defaultSite);
    }
  }

  private onSiteSelected(site: Site): void {
    this.loadSiteReferentials(site, this.selectedClient?.id).then(() => {
      if (!site || typeof site === 'string' || !site.id) {
        return;
      }

      this.editForm.get('sender.address').setValue(this.resolveDefaultSenderAddress(site));
      this.getArray('receivers').controls.forEach(control => control.get('address').setValue(null));
      this.editForm.get('urgencyLevel').setValue(null);
      this.transportTypesSelected = [];
      this.checkIfSelected();
    });
  }

  /** L'adresse par défaut du donneur d'ordre n'est appliquée qu'à la création. */
  private resolveDefaultSenderAddress(site: Site): Address {
    if (this.isEditMode) {
      return null;
    }

    return this.senders?.find(address => address.id === site.address?.id) ?? null;
  }

  private resetClientDependentFields(): void {
    this.editForm.get('site').setValue(null, { emitEvent: false });
    this.editForm.get('urgencyLevel').setValue(null, { emitEvent: false });
    this.transportTypesSelected = [];
    this.canAddOtherAddress = true;
    this.checkIfSelected();
  }

  // ---------------------------------------------------------------------------
  // Construction du formulaire
  // ---------------------------------------------------------------------------

  private buildForm(order: Order | null): void {
    this.editForm = this.fb.group({
      ordererComment: [order?.ordererComment ?? null, [Validators.maxLength(this.commentMaxLength)]],
      urgencyLevel: [this.resolveUrgencyLevel(order), [Validators.required]],
      schedule: this.fb.array([this.buildScheduleGroup(order)]),
      sender: this.buildSenderGroup(order?.sender),
      receivers: this.fb.array([], Validators.required),
      ordererPhone: [order?.ordererPhone ?? null, [Validators.maxLength(this.stringMaxLength)]],
      ordererName: [order?.ordererName ?? null, [Validators.maxLength(this.stringMaxLength)]],
      callDate: [this.toCallTime(order)],
      site: [this.resolveSite(order), [Validators.required]]
    });

    const receivers = order?.receivers?.length ? order.receivers : [null];
    receivers.forEach(receiver => this.getArray('receivers').push(this.buildReceiverGroup(receiver)));

    this.transportTypesSelected = this.resolveTransportTypes(order);
    this.checkIfSelected();

    this.wireFormSideEffects();
    this.updateInvalidMessage();
  }

  private wireFormSideEffects(): void {
    this.addSubscription(
      this.editForm.valueChanges.subscribe(() => this.updateInvalidMessage())
    );

    const schedule = this.getArray('schedule').at(0);

    // mustCheckDate ne doit se lever que si l'utilisateur modifie lui-même la date.
    this.addSubscription(
      schedule.get('startDate').valueChanges.subscribe(() => this.mustCheckDate = true)
    );
    this.addSubscription(
      schedule.get('startTime').valueChanges.subscribe(() => this.mustCheckDate = true)
    );

    const siteControl = this.editForm.get('site');

    // startWith + skip(1) : la valeur déjà présente sert de référence et n'est pas traitée comme un changement.
    this.addSubscription(
      siteControl.valueChanges
        .pipe(
          startWith(siteControl.value),
          distinctUntilChanged((previous, current) => this.siteKey(previous) === this.siteKey(current)),
          skip(1)
        )
        .subscribe((site: Site) => this.onSiteSelected(site))
    );
  }

  private siteKey(site: Site | string): string {
    if (!site) {
      return null;
    }

    return typeof site === 'string' ? site : site.id;
  }

  private buildScheduleGroup(order: Order | null): UntypedFormGroup {
    const start: moment.Moment = order?.startDate
      ? moment(new Date(order.startDate))
      : moment(new Date()).add(20, 'minutes');

    return this.fb.group({
      startDate: [start, Validators.required],
      startTime: [start.format('HH:mm'), Validators.required]
    });
  }

  private buildSenderGroup(sender?: any): UntypedFormGroup {
    return this.fb.group({
      id: [sender?.id ?? null],
      address: [this.resolveStepAddress(sender, this.senders), [Validators.required]],
      otherAddress: [sender?.otherAddress ?? null],
      service: [sender?.service ?? null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      ordererComment: [sender?.ordererComment ?? null, [Validators.maxLength(this.commentMaxLength)]],
      isPickup: [true],
      isDelivery: [false]
    }, Validators.required);
  }

  private buildReceiverGroup(receiver?: any): UntypedFormGroup {
    return this.fb.group({
      id: [receiver?.id ?? null],
      address: [this.resolveStepAddress(receiver, this.receivers), [Validators.required]],
      otherAddress: [receiver?.otherAddress ?? null],
      service: [receiver?.service ?? null, [Validators.required]],
      ordererComment: [receiver?.ordererComment ?? null, [Validators.maxLength(this.commentMaxLength)]],
      type: [this.resolveStepType(receiver), [Validators.required]]
    });
  }

  /** Une adresse libre est portée par le même contrôle que l'adresse référencée, sous forme de string. */
  private resolveStepAddress(step: any, knownAddresses: Address[]): Address | string {
    if (!step) {
      return null;
    }

    if (step.otherAddress) {
      return step.otherAddress;
    }

    return knownAddresses?.find(address => address.id === step.address?.id) ?? step.address ?? null;
  }

  /** Le choix enlèvement/livraison n'est proposé qu'à partir de 2 étapes, et la dernière doit être une livraison. */
  private resolveStepType(step: any): string {
    return step?.isPickup ? 'isPickup' : 'isDelivery';
  }

  private resolveSite(order: Order | null): Site {
    if (!order?.site) {
      return null;
    }

    return this.sites?.find(site => site.id === order.site.id) ?? order.site;
  }

  private resolveUrgencyLevel(order: Order | null): DictionaryValue {
    if (!order?.urgency) {
      return null;
    }

    return this.urgencyLevels?.find(urgency => urgency.id === order.urgency.id) ?? null;
  }

  private resolveTransportTypes(order: Order | null): DictionaryValue[] {
    if (!order?.transportTypes?.length) {
      return [];
    }

    const transportType = order.transportTypes[0];
    return [this.allTransportTypes?.find(known => known.id === transportType.id) ?? transportType];
  }

  private toCallTime(order: Order | null): string {
    return order?.callDate ? moment(new Date(order.callDate)).format('HH:mm') : null;
  }

  private updateInvalidMessage(): void {
    this.invalidFields = this.findInvalidControls();
    this.invalidMessage = 'Le formulaire est incomplet/incorrect';
    this.invalidFields.forEach(field => this.invalidMessage += ' ' + field);
  }

  public findInvalidControls(): string[] {
    const invalid: string[] = [];
    const controls = this.editForm.controls;

    for (const name of Object.keys(controls)) {
      if (!controls[name].invalid) {
        continue;
      }

      if (name === 'sender') {
        invalid.push(` | Lieu d'enlèvement incomplet (${this.describeInvalidFields(controls[name])})`);
      } else if (name === 'receivers') {
        this.getArray('receivers').controls.forEach((receiver, index) => {
          if (receiver.invalid) {
            invalid.push(` | Étape ${index + 2} incomplète (${this.describeInvalidFields(receiver)})`);
          }
        });
      } else {
        invalid.push(this.translateFields(name));
      }
    }

    const receivers = this.editForm.value.receivers;
    const lastReceiver = receivers[receivers.length - 1];

    if (lastReceiver?.type === 'isPickup') {
      invalid.push(' | La dernière étape est un enlèvement');
    }

    if (this.transportTypesSelected.length === 0) {
      invalid.push(' | Il n\'y a aucun type de produit');
    }

    return invalid;
  }

  private describeInvalidFields(group: AbstractControl): string {
    const labels = {
      address: 'adresse',
      service: 'service',
      type: 'enlèvement ou livraison',
      ordererComment: 'commentaire'
    };
    const controls = (group as UntypedFormGroup).controls;

    return Object.keys(controls)
      .filter(name => controls[name].invalid)
      .map(name => labels[name] ?? name)
      .join(', ');
  }

  translateFields(name: string): string {
    const labels = {
      site: 'Donneur d\'ordre',
      urgencyLevel: 'Niveau d\'urgence',
      schedule: 'Date et heure de départ',
      ordererName: 'Nom du demandeur',
      ordererPhone: 'Téléphone du demandeur',
      ordererComment: 'Commentaire sur la course',
      callDate: 'Heure d\'appel'
    };

    return ' | ' + (labels[name] ?? name);
  }

  private scheduleToDate(schedule: any): Date {
    if (!schedule.startDate || !schedule.startTime || typeof schedule.startDate === 'string') {
      return null;
    }

    const startDate: moment.Moment = schedule.startDate.local();
    const startTime: number[] = schedule.startTime.split(':').map(s => parseInt(s, 10));
    return startDate.hours(startTime[0]).minutes(startTime[1]).utc().toDate();
  }

  resetStartDate(control) {
    control.get('startDate').reset();
  }

  onAddReceiver(): void {
    this.getArray('receivers').push(this.buildReceiverGroup());
  }

  onRemoveReceiver(index: number): void {
    this.getArray('receivers').removeAt(index);
  }

  remove(fruit: DictionaryValue): void {
    const index = this.transportTypesSelected.indexOf(fruit);

    if (index >= 0) {
      this.transportTypesSelected.splice(index, 1);
    }

    this.checkIfSelected();
  }

  // Une transmission ne porte qu'un seul type de produit, mais l'API en attend une liste.
  selected(event: MatAutocompleteSelectedEvent): void {
    document.getElementById("deviceChipList").blur();

    this.transportTypesSelected = [event.option.value];
    this.checkIfSelected();

    this.deviceInput.nativeElement.value = '';
  }

  checkIfSelected() {
    this.filteredTransportTypes = this.filteredTransportTypes.pipe(
      map(array => {
        return array.map(element => {
          element.selected = this.transportTypesSelected.includes(element);
          return element;
        });
      })
    );
  }

  private _filter(value?): DictionaryValue[] {
    if (value.name) {
      return this.allTransportTypes.filter(el => el.id === value.id);
    }

    const filterValue = value.toLowerCase();
    return this.allTransportTypes.filter(transportType => transportType.name.toLowerCase().indexOf(filterValue) === 0);
  }

  clearDevicesSelected(): void {
    this.transportTypesSelected = [];
    this.checkIfSelected();
  }

  getClientControl(control: string): UntypedFormControl {
    return this.clientForm.get(control) as UntypedFormControl;
  }

  isTypeOf(type: string, value: any): boolean {
    return typeof value === type;
  }
}